export const headDocumentation = {
  lists: [
    {
      id: "ipad-tutorial",
      name: "Exam on iPad",
      content: `# 📱 How to Fill Out the PDF Mock Exam on an iPad  

You can complete your mock exam on an iPad using **Apple's Markup Tool**, **Adobe Acrobat**, or a third-party PDF editor. Follow these steps:  

---

## **📝 Method 1: Using Apple's Built-in Markup Tool (Recommended)**  
### **Step 1: Open the PDF in Files or Notes**  
- Locate the mock exam PDF in the **Files** app or open it from your email.  
- Tap on the PDF to open it.  

### **Step 2: Enter Markup Mode**  
- Tap the **Markup (🖊️ pen icon)** in the top right corner.  
- Select the **Text Tool (T icon)** to type answers or use the **Pen Tool** to write.  

### **Step 3: Save & Submit**  
- Tap **Done** and choose **Save File** or **Share** to send your completed exam.  
![image1](assets/illustration-analysis.svg)
---

## **📄 Method 2: Using Adobe Acrobat Reader**  
### **Step 1: Download & Open Adobe Acrobat**  
- Install **Adobe Acrobat Reader** from the App Store.  
- Open the PDF inside the app.  

### **Step 2: Fill in Your Answers**  
- Tap **Fill & Sign** from the bottom menu.  
- Use the **Text Tool (T icon)** to type answers.  
- If required, use the **Pen Tool** to draw or sign.  

### **Step 3: Save & Submit**  
- Tap **Save** and export the file via email or upload it.  

---

## **🌍 Method 3: Using a Third-Party PDF Editor**  
If you prefer, use apps like:  
✅ **GoodNotes**  
✅ **Notability**  
✅ **PDF Expert**  

### **Step 1: Open the PDF in Your Preferred App**  
- Import the PDF from **Files**, **Google Drive**, or **Email**.  

### **Step 2: Fill Out the Exam**  
- Use the **text tool** to type responses.  
- Use the **stylus tool** (Apple Pencil recommended) to write directly.  

### **Step 3: Save & Submit**  
- Save the file and export it via **Email**, **Google Drive**, or the submission portal.  

---

### **✨ Pro Tips:**  
✅ **Apple Markup** is the fastest option for quick edits.  
✅ **Adobe Acrobat** is ideal for precise text input.  
✅ **GoodNotes & Notability** offer a smooth handwriting experience.  

---

## **📤 Uploading to the Portal**
### **Step 1: Access the Submission Page**
- Log in to your AcademIX account.
- Navigate to the exam submission page.

### **Step 2: Upload Your Completed Work**
- Click on the upload area or drag and drop your file.
- You can submit either a single PDF or a ZIP file containing multiple PDFs.
- If you have multiple PDF files to submit, compress them into a ZIP file.
- Ensure your file is in PDF or ZIP format and under 100MB.
- Check all required confirmation boxes.
- Click the Submit button to complete your submission.


---
## 🚀 **Now you're ready to complete and submit your mock exam on your iPad!**  
`,
    },
    {
      id: "laptop-tutorial",
      name: "Exam on Laptop",
      content: `# 💻 How to Fill Out the PDF Mock Exam on a Laptop  

First let's download the exam paper first:

##

![image2](assets/illustration-analysis.svg)

##


You can complete your mock exam on a laptop using **Adobe Acrobat Reader**, **Microsoft Edge**, or **third-party PDF editors**. Follow these steps:  

---

## **📝 Method 1: Using Adobe Acrobat Reader (Recommended)**  
### **Step 1: Download & Open Adobe Acrobat**  
- Install **Adobe Acrobat Reader** if you haven't already.  
- Open the mock exam PDF inside the application.  

### **Step 2: Fill in Your Answers**  
- Click on **Fill & Sign** from the right panel.  
- Use the **Text Tool (T icon)** to type your answers in the blanks.  
- If necessary, use the **Pen Tool** to draw or sign.  

### **Step 3: Save & Submit**  
- Click **File > Save As** to save your completed exam.  
- Submit it via email or upload it as instructed.  

---

## **🌐 Method 2: Using a Web Browser (Microsoft Edge or Chrome)**  
### **Step 1: Open the PDF in Your Browser**  
- Right-click the PDF and select **Open with > Microsoft Edge** or **Google Chrome**.  

### **Step 2: Add Your Answers**  
- Click the **"Add Text"** option to type responses.  
- Use the **Draw Tool** for signatures or annotations.  

### **Step 3: Save & Submit**  
- Click **Save** and choose a destination on your computer.  
- Attach the file to an email or upload it to the submission portal.  


##

![image3](assets/illustration-analysis.svg)

##


---

## **📄 Method 3: Using a Third-Party PDF Editor**  
If you need more features, try:  
✅ **Smallpdf** (Online)  
✅ **PDFescape** (Online)  
✅ **Foxit PDF Editor** (Offline) 

##

### **Step 1: Open the PDF in Your Preferred Editor**  
- Upload or open the file inside the application.  

### **Step 2: Fill Out the Exam**  
- Use the **Text Tool** to type answers.  
- Use the **Pen Tool** for annotations or signatures.  

### **Step 3: Save & Submit**  
- Export the final version and submit it via the designated method.  

---

### **✨ Pro Tips:**  
✅ **Adobe Acrobat** provides the best experience for typing and signing.  
✅ **Microsoft Edge** is a quick and easy solution without extra software.  
✅ **Online editors** work well if you don't want to install apps.  

---

## **📤 Uploading to the Portal**
### **Step 1: Access the Submission Page**
- Log in to your AcademIX account.
- Navigate to the exam submission page.

### **Step 2: Upload Your Completed Work**
- Click on the upload area or drag and drop your file.
- You can submit either a single PDF or a ZIP file containing multiple PDFs.
- If you have multiple PDF files to submit, compress them into a ZIP file.
- Ensure your file is in PDF or ZIP format and under 100MB.
- Check all required confirmation boxes.
- Click the Submit button to complete your submission.


---

## 🚀 **Now you're ready to complete and submit your mock exam on your laptop!**  

`,
      //       content: [
      //         {
      //           type: 'block',
      //           body: `📖 How to Fill Out the PDF Mock Exam on an iPad
      // Easily complete your mock exam on an iPad using Apple's built-in tools or a third-party app. Follow these simple steps:`
      //         }
      //       ]
    },
    {
      id: "paper-tutorial",
      name: "Exam on Paper",
      content: `# 📄 How to Complete and Submit the Mock Exam on Paper  

If you prefer a traditional approach, you can print the exam, complete it by hand, and then scan and upload it. Follow these steps:  

---

## **🖨️ Step 1: Printing the Exam**  
### **Printing Instructions**  
- Download the PDF exam from your AcademIX dashboard.
- Ensure your printer has sufficient paper and ink/toner.
- Print in high quality (recommended settings: black & white, 600 DPI or higher).
- Check that all pages printed correctly and are legible.

---

## **✏️ Step 2: Completing the Exam**  
### **Working on Paper Tips**  
- Use a blue or black pen for all answers (pencil may not scan well).
- Write clearly and stay within the designated answer spaces.
- If you make a mistake, cross it out neatly rather than using correction fluid.
- Number your answers clearly if working on blank paper.
- Ensure your name and student ID are on every page.

---

## **📱 Step 3: Scanning Your Completed Exam**  
### **Option 1: Using Microsoft Lens (Recommended)**  
- Download **Microsoft Lens** from your device's app store.
- Open the app and select **Document** mode.
- Position your phone camera over the first page and capture it.
- Adjust the borders if needed for a clean scan.
- Continue scanning all pages of your exam.
- Save the document as a PDF.

### **Option 2: Using CamScanner**  
- Download **CamScanner** from your device's app store.
- Open the app and tap the camera icon.
- Capture all pages of your exam.
- Use the auto-enhancement features for better readability.
- Export the document as a single PDF file.

### **Option 3: Using a Traditional Scanner**  
- Place your exam on the scanner bed.
- Set the scanner to PDF format at 300 DPI (minimum).
- Scan all pages and save as a single PDF document.

---

## **📤 Step 4: Uploading to the Portal**  
### **Submission Instructions**  
- Log in to your AcademIX account.
- Navigate to the exam submission page.
- Click on the upload area or drag and drop your file.
- You can submit either a single PDF or a ZIP file containing multiple PDFs.
- If you have multiple scanned PDFs, compress them into a ZIP file.
- Ensure your file is in PDF or ZIP format and under 100MB.
- Check all required confirmation boxes.
- Click the Submit button to complete your submission.


---

### **✨ Pro Tips:**  
✅ **Scan in a well-lit area** to avoid shadows and improve readability.  
✅ **Review your scan** before uploading to ensure all content is visible and legible.  
✅ **Test your scanning app** before exam day to become familiar with the process.  
✅ **Keep the original paper copy** until your submission is confirmed.  

---

## 🚀 **Now you're ready to complete and submit your mock exam on paper!**  
`,
    },
  ],
};
